import { formatValidQueryParamUrlCommaSeparated } from "utils/common";

import { emptySplitApi } from "./empty-split-api";
import {
  CallBackAttempt,
  CallBackAttemptCreate,
  CallBackEntriesFilter,
  CallBackEntriesResponse,
  CallBackEntry,
  CallBackEntryCreate,
  CallBackEntryUpdate,
  CallBackReason,
  CallBackReasonCreate,
  CallBackReasonUpdate,
  CallBackResolutionOption,
  CallBackResolutionOptionCreate,
  CallBackResolutionOptionUpdate
} from "./types/call-back.type";

export interface CallBackPracticeSettings {
  auto_expire_enabled: boolean;
  auto_expire_days: number;
}

const ENTRY_QUERY =
  "{id,details,due_date,priority,is_required,status,source,resolution_note,resolved_at," +
  "created_at,last_visit_at,next_appointment_at,attempts_count,last_attempt_outcome,last_attempt_at," +
  "patient{id,first_name,last_name,preferred_name,phone,do_not_call,block_automation}," +
  "clinic{id,name},reason{id,name,color,is_system,default_due_in_days,default_priority," +
  "default_is_required,resolution_options},assigned_to{id,first_name,last_name}," +
  // created_by is deliberately NOT sub-projected: the backend renders it via a
  // FixedField (audit mixin), which RestQL cannot project into — `created_by{...}`
  // 400s ("not a nested field") on any non-empty list. Flat returns the full object.
  "resolved_by{id,first_name,last_name},created_by,resolution{id,label,category}}";

const callBackApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getCallBackEntries: builder.query<
      CallBackEntriesResponse,
      CallBackEntriesFilter
    >({
      query: (filter) =>
        `marketing/call-back-entries/?query=${ENTRY_QUERY}&${formatValidQueryParamUrlCommaSeparated(filter)}`,
      providesTags: ["CallBackEntries"]
    }),
    // Tile counts reuse the list endpoint with limit=0 — the envelope carries the count
    // with no result rows (backend ZeroLimitOffsetPagination).
    getCallBackEntriesCount: builder.query<
      CallBackEntriesResponse,
      CallBackEntriesFilter
    >({
      query: (filter) =>
        `marketing/call-back-entries/?query={id}&limit=0&${formatValidQueryParamUrlCommaSeparated(filter)}`,
      providesTags: ["CallBackEntries"]
    }),
    getCallBackEntry: builder.query<CallBackEntry, number>({
      query: (id) => `marketing/call-back-entries/${id}/?query=${ENTRY_QUERY}`,
      providesTags: (result) => [
        { type: "CallBackEntries" as const, id: result?.id }
      ]
    }),
    createCallBackEntry: builder.mutation<CallBackEntry, CallBackEntryCreate>({
      query: (body) => ({
        url: "marketing/call-back-entries/",
        method: "POST",
        body
      }),
      invalidatesTags: ["CallBackEntries"]
    }),
    updateCallBackEntry: builder.mutation<CallBackEntry, CallBackEntryUpdate>({
      query: ({ id, ...body }) => ({
        url: `marketing/call-back-entries/${id}/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: ["CallBackEntries"]
    }),
    getCallBackAttempts: builder.query<CallBackAttempt[], number>({
      query: (entryId) => `marketing/call-back-entries/${entryId}/attempts/`,
      providesTags: ["CallBackAttempts"]
    }),
    createCallBackAttempt: builder.mutation<
      CallBackAttempt,
      CallBackAttemptCreate
    >({
      query: ({ entryId, ...body }) => ({
        url: `marketing/call-back-entries/${entryId}/attempts/`,
        method: "POST",
        body
      }),
      // Deliberately does NOT invalidate CallBackEntries: the log-call dialog runs
      // attempt + resolve sequentially, and two entry invalidations per save refetch
      // the list, all tile counts, and the home widget twice. The dialog dispatches
      // a single CallBackEntries invalidation itself after the combined save.
      invalidatesTags: ["CallBackAttempts"]
    }),
    getCallBackReasons: builder.query<
      { count: number; results: CallBackReason[] },
      { is_active?: boolean; limit?: number }
    >({
      query: (filter) =>
        `marketing/call-back-reasons/?limit=100&${formatValidQueryParamUrlCommaSeparated(filter)}`,
      providesTags: ["CallBackReasons"]
    }),
    createCallBackReason: builder.mutation<
      CallBackReason,
      CallBackReasonCreate
    >({
      query: (body) => ({
        url: "marketing/call-back-reasons/",
        method: "POST",
        body
      }),
      invalidatesTags: ["CallBackReasons"]
    }),
    updateCallBackReason: builder.mutation<
      CallBackReason,
      CallBackReasonUpdate
    >({
      query: ({ id, ...body }) => ({
        url: `marketing/call-back-reasons/${id}/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: ["CallBackReasons", "CallBackEntries"]
    }),
    createCallBackResolutionOption: builder.mutation<
      CallBackResolutionOption,
      CallBackResolutionOptionCreate
    >({
      query: ({ reasonId, ...body }) => ({
        url: `marketing/call-back-reasons/${reasonId}/resolution-options/`,
        method: "POST",
        body
      }),
      invalidatesTags: ["CallBackReasons"]
    }),
    updateCallBackResolutionOption: builder.mutation<
      CallBackResolutionOption,
      CallBackResolutionOptionUpdate
    >({
      query: ({ reasonId, id, ...body }) => ({
        url: `marketing/call-back-reasons/${reasonId}/resolution-options/${id}/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: ["CallBackReasons"]
    }),
    // Narrow practice read/write for the flag-gated settings page only — keeps the
    // RestQL blast radius of a frontend-first deploy to this single request
    // (same rationale as getPracticeAuthSettings in services/practices.ts).
    getPracticeCallBackSettings: builder.query<
      { id: number; call_back_settings: CallBackPracticeSettings | null },
      number
    >({
      query: (id) => ({
        url: `/practices/${id}/?query={id,call_back_settings}`
      }),
      providesTags: ["Clinics"]
    }),
    updatePracticeCallBackSettings: builder.mutation<
      unknown,
      { id: number; call_back_settings: CallBackPracticeSettings }
    >({
      query: ({ id, call_back_settings }) => ({
        url: `/practices/${id}/`,
        method: "PATCH",
        body: { call_back_settings }
      }),
      invalidatesTags: ["Clinics"]
    })
  })
});

export const {
  useGetCallBackEntriesQuery,
  useLazyGetCallBackEntriesQuery,
  useGetCallBackEntriesCountQuery,
  useGetCallBackEntryQuery,
  useLazyGetCallBackEntryQuery,
  useCreateCallBackEntryMutation,
  useUpdateCallBackEntryMutation,
  useGetCallBackAttemptsQuery,
  useCreateCallBackAttemptMutation,
  useGetCallBackReasonsQuery,
  useCreateCallBackReasonMutation,
  useUpdateCallBackReasonMutation,
  useCreateCallBackResolutionOptionMutation,
  useUpdateCallBackResolutionOptionMutation,
  useGetPracticeCallBackSettingsQuery,
  useUpdatePracticeCallBackSettingsMutation
} = callBackApi;

export default callBackApi;
